<script setup lang="ts">
import type { ListingLayoutOption } from './types';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed, ref } from 'vue';
import { IconPicker } from '#layers/ui/public';

interface Props {
	hasChanges?: boolean;
	namePlaceholder?: string;
	layoutOptions: ListingLayoutOption[];
}

const { hasChanges = false, layoutOptions } = defineProps<Props>();

defineEmits<{
	reset: [];
	saveForEveryone: [];
}>();

const open = defineModel<boolean>('open', { default: false });

const layoutBlocks = computed(() => {
	const order: string[] = [BuiltinBlockIds.LayoutTable, BuiltinBlockIds.LayoutCards, BuiltinBlockIds.LayoutCalendar];

	function orderIndex(id: string) {
		const index = order.indexOf(id);
		return index === -1 ? order.length : index;
	}

	return layoutOptions.toSorted((a, b) => orderIndex(a.id) - orderIndex(b.id));
});

const layoutId = defineModel<string>('layoutId', { default: BuiltinBlockIds.LayoutTable });
const name = defineModel<string>('name');
const icon = defineModel<string>('icon');

const defaultIcon = computed(() => layoutOptions.find((option) => option.id === layoutId.value)?.icon ?? undefined);

const state = ref({});
</script>

<template>
	<DPopover v-model:open="open" :ui="{ content: 'overflow-hidden' }">
		<DTooltip :text="$t('listing.views.display_options.title')" :disabled="open">
			<DButton
				color="neutral"
				variant="outline"
				:aria-label="$t('listing.views.display_options.title')"
				square
				:pressed="open"
				size="sm"
				icon="lucide:sliders-horizontal"
				:indicator="hasChanges"
				data-testid="display-options-button"
			/>
		</DTooltip>
		<template #content>
			<DAnimatedSheet dimension="height" class="flex w-90 flex-col">
				<div class="flex flex-col gap-3 p-4">
					<div class="text-xs font-semibold tracking-wide text-dimmed uppercase">
						{{ $t('listing.views.display_options.layout_options') }}
					</div>
					<DTileRadioGroup
						v-model="layoutId"
						class="grid-cols-3 gap-3"
						:items="layoutBlocks"
						value-key="id"
						label-key="name"
						size="xs"
						:ui="{
							item: 'flex-col items-center justify-center gap-1.5 px-1.5 py-3',
							itemIcon: 'size-6 rounded-sm',
							itemTextWrapper: 'w-auto items-center',
							itemLabel: 'text-xs font-semibold',
						}"
					/>
				</div>
				<DSeparator />
				<div class="p-4">
					<DForm
						:state
						layout="side-by-side-fixed"
						size="sm"
						auto-focus-first-input
						:ui="{ layout: 'gap-y-3' }"
						:field-ui="{ label: 'flex min-h-8 items-center' }"
					>
						<DFormField :label="$t('listing.views.name_label')" :error-pattern="/name|icon/">
							<DFieldGroup>
								<IconPicker
									v-model="icon"
									:placeholder-icon="defaultIcon"
									pick-emoji
									spacing="comfortable"
								/>
								<DInput
									v-model="name"
									data-testid="view-name-input"
									:placeholder="namePlaceholder"
									autofocus
									autocomplete="off"
									class="w-full"
								/>
							</DFieldGroup>
						</DFormField>
						<slot />
					</DForm>
				</div>
				<template v-if="hasChanges">
					<DSeparator />
					<div class="flex items-center justify-end gap-1.5 p-1.5" data-testid="display-options-save-footer">
						<DTooltip :text="$t('listing.views.save.reset_tooltip')">
							<DButton
								variant="ghost"
								color="neutral"
								size="sm"
								data-testid="display-options-reset-button"
								@click="$emit('reset')"
							>
								{{ $t('listing.views.save.reset') }}
							</DButton>
						</DTooltip>
						<DButton
							size="sm"
							data-testid="display-options-save-for-everyone-button"
							@click="$emit('saveForEveryone')"
						>
							{{ $t('listing.views.save.save_for_everyone') }}
						</DButton>
					</div>
				</template>
			</DAnimatedSheet>
		</template>
	</DPopover>
</template>
